import type { ScanEventDescription } from './fh-vitals-events'
import type { MindsetSignsMessage } from './mindset-types'

/**
 * The vendor's per-frame quality codes, mapped to the same `ScanEventDescription` the FaceHeart
 * events produce — so one banner on one screen serves both SDKs and a patient reads the same
 * sentence whichever vendor their scan happens to run.
 *
 * It names TRANSLATION KEYS and not sentences, for the reason fh-vitals-events.ts gives at length:
 * the scan surface localises from the token (architecture §9), and a literal here is a paragraph of
 * English in the middle of a Spanish page that nothing goes red for.
 *
 * Every code is advisory. Unlike FaceHeart's `_restart_` and `_connection_close_`, none of these
 * ends the session — the vendor keeps measuring and stops itself when it has what it needs or when
 * its attempts run out, so `endsSession` is false throughout and the session runner's own window is
 * what bounds the scan.
 */
export function describeMindsetSigns(message: MindsetSignsMessage): ScanEventDescription | null {
  return describeMindsetSignals(message)[0] ?? null
}

/**
 * EVERY signal the vendor is currently raising, not the first one.
 *
 * The vendor sends a whole `dataStatus` array per frame and it routinely holds more than one warning
 * at once - "stay still" and "improve your lighting" arrive together, because someone adjusting their
 * grip in a dim room is doing both. Showing only the first made the second invisible, and a patient who
 * fixes the lighting and sees the same screen has no way to know the app noticed.
 *
 * Deduplicated BY SENTENCE rather than by code, because the vendor has three separate codes -
 * `E-FDM-042`, `W-RES-073`, `W-RES-078` - whose message is "please keep face centered" in all three.
 * Three identical lines on a patient's screen is a bug that looks like a stutter.
 *
 * Unmapped WARNING codes are logged and not shown. A patient reads a sentence this app wrote
 * (architecture §9): the vendor's own English would be untranslated on a Spanish page and is written
 * for whoever integrates the SDK. The log is how the next unmapped code gets found - which is how
 * these three were (docs/decisions.md D-28).
 */
export function describeMindsetSignals(message: MindsetSignsMessage): ScanEventDescription[] {
  const described: ScanEventDescription[] = []
  const seen = new Set<string>()

  for (const status of message.dataStatus ?? []) {
    const code = status.code ?? ''
    const description = describeCode(code)

    if (!description) {
      if (status.severity === 'warning') {
        console.warn('[mindset] an unmapped warning code, so the patient was told nothing', {
          code,
          vendorMessage: status.message,
        })
      }
      continue
    }

    const key = description.messageKey
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    described.push(description)
  }

  return described
}

function describeCode(code: string): ScanEventDescription | null {
  switch (code) {
    // "Stay still" — the vendor's data-quality monitor has seen motion.
    case 'E-DQM-021':
      return { messageKey: 'motionDetail', endsSession: false }

    // Lighting. Its own sentence rather than the motion one: "hold still" is unhelpful and slightly
    // insulting to somebody sitting perfectly still in a dark room.
    case 'E-DQM-022':
      return { messageKey: 'lightingDetail', endsSession: false }

    case 'E-FDM-041':
      return { messageKey: 'faceLossDetail', endsSession: false }

    // Off-centre, in the vendor's three separate codes for it. Captured from a real run against the
    // real SDK, where all three arrived with the message "Please keep face centered" and NONE of them
    // was mapped here - so the one thing a patient most often needs to be told was the one thing this
    // app said nothing about (docs/decisions.md D-28).
    //
    // Three cases and one sentence: the vendor's own texts are identical, and inventing three
    // different ways to say "centre your face" would be this app pretending to know a difference the
    // vendor does not describe.
    case 'E-FDM-042':
    case 'W-RES-073':
    case 'W-RES-078':
      return { messageKey: 'faceCentreDetail', endsSession: false }

    case 'W-RES-079':
      return { messageKey: 'faceTooFarDetail', endsSession: false }

    case 'W-RES-080':
      return { messageKey: 'faceTooCloseDetail', endsSession: false }

    // Everything else, including the ready/early-stop codes (S-HRT-061, S-RES-070, S-BPR-090,
    // S-SPO-100), which report progress rather than trouble. Returning null leaves the banner as it
    // was: a scan going well should not flash a "scan update" at the patient every few frames.
    default:
      return null
  }
}
